import React, { useState, useRef } from "react";
import { useAuth, apiError } from "@/context/AuthContext";
import { useI18n } from "@/context/LanguageContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, User, KeyRound, ShieldCheck, Sparkles, IdCard, Check, ArrowLeft, Globe } from "lucide-react";
import { toast } from "sonner";
import { LegalFooter } from "@/components/LegalFooter";

const AuthPage = () => {
  const { login, register } = useAuth();
  const { t, lang, setLang, languages } = useI18n();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ username: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [ageOk, setAgeOk] = useState(false);
  const [idImage, setIdImage] = useState(null);
  const idRef = useRef();

  // forgot password state
  const [fpEmail, setFpEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [tokenIssued, setTokenIssued] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onIdFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez choisir une image (photo de la pièce d'identité).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 8 Mo).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setIdImage(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      if (!ageOk) return toast.error(t("auth.ageError"));
      if (!idImage) return toast.error(t("auth.idError"));
    }
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.username, form.email, form.password, {
          age_confirmed: ageOk,
          kyc_id_image_base64: idImage,
        });
      }
      toast.success(t("auth.welcome"));
    } catch (err) {
      toast.error(apiError(err, "Échec de l'authentification"));
    } finally {
      setBusy(false);
    }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: fpEmail });
      setTokenIssued(true);
      if (data.reset_token) {
        setResetToken(data.reset_token);
        toast.success("Lien de réinitialisation généré (démo).");
      } else {
        toast.success(data.message);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, password: newPassword });
      toast.success("Mot de passe réinitialisé. Connectez-vous.");
      setMode("login");
      setTokenIssued(false);
      setResetToken("");
      setNewPassword("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const LangPicker = () => (
    <div className="flex items-center gap-1.5">
      <Globe className="h-4 w-4 text-muted-foreground" />
      <Select value={lang} onValueChange={setLang}>
        <SelectTrigger className="h-8 w-[128px] rounded-lg text-xs" data-testid="auth-language-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {languages.map((l) => (
            <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>
              <span className="mr-1">{l.flag}</span> {l.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left hero (desktop) */}
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden bg-[#05070d] text-white">
        <div className="relative z-10">
          <img src="/photopay-logo.jpeg" alt="MIHOD PAY" className="w-full max-w-md rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.85)]" />
          <h1 className="font-display text-4xl xl:text-5xl mt-16 leading-tight">
            {t("auth.tagline")}
          </h1>
          <p className="mt-4 text-white/70 max-w-sm">{t("auth.subtitle")}</p>
        </div>
        <div className="relative z-10 space-y-2 text-sm text-white/70">
          <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(214_100%_62%)]" /> Photos jamais visibles avant paiement</p>
          <p className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(214_100%_62%)]" /> Revenus reversés instantanément</p>
        </div>
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(600px circle at 20% 20%, hsla(214,100%,62%,0.22), transparent 60%), radial-gradient(500px circle at 85% 85%, hsla(0,92%,58%,0.14), transparent 60%)" }} />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-sm p-6 rounded-2xl shadow-[0_18px_50px_-28px_rgba(15,23,42,0.45)]">
          <div className="flex items-center justify-between mb-4">
            <div className="lg:hidden">
              <img src="/photopay-logo.jpeg" alt="MIHOD PAY" className="h-10 w-auto rounded-lg" />
            </div>
            <div className="ml-auto"><LangPicker /></div>
          </div>

          {mode === "forgot" ? (
            <div className="space-y-4">
              <button onClick={() => { setMode("login"); setTokenIssued(false); }} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back">
                <ArrowLeft className="h-4 w-4" /> {t("auth.backToLogin")}
              </button>
              <h2 className="font-display text-xl">{t("auth.reset")}</h2>

              {!tokenIssued ? (
                <form onSubmit={requestReset} className="space-y-4">
                  <p className="text-sm text-muted-foreground">{t("auth.resetHint")}</p>
                  <div className="space-y-1.5">
                    <Label htmlFor="fp-email">{t("auth.email")}</Label>
                    <div className="relative">
                      <Mail className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                      <Input id="fp-email" type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} placeholder="vous@exemple.com" className="pl-9 rounded-xl" required data-testid="forgot-email" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full rounded-xl" disabled={busy} data-testid="forgot-submit">
                    {busy ? t("common.pleaseWait") : t("auth.reset")}
                  </Button>
                </form>
              ) : (
                <form onSubmit={doReset} className="space-y-4">
                  <div className="rounded-xl bg-secondary p-3 text-xs text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Lien de réinitialisation (démo)</p>
                    <p>Aucun email n'est envoyé dans cette démo — le jeton est pré-rempli ci-dessous.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-token">Jeton</Label>
                    <Input id="reset-token" value={resetToken} onChange={(e) => setResetToken(e.target.value)} className="rounded-xl text-xs" required data-testid="reset-token" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-pass">{t("auth.newPassword")}</Label>
                    <div className="relative">
                      <KeyRound className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                      <Input id="reset-pass" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" className="pl-9 rounded-xl" required minLength={6} data-testid="reset-password" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full rounded-xl" disabled={busy} data-testid="reset-submit">
                    {busy ? t("common.pleaseWait") : t("auth.reset")}
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <>
              <div className="flex rounded-xl bg-secondary p-1 mb-6">
                <button
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === "login" ? "bg-card shadow-sm" : "text-muted-foreground"}`}
                  onClick={() => setMode("login")}
                  data-testid="tab-login"
                >{t("auth.login")}</button>
                <button
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === "register" ? "bg-card shadow-sm" : "text-muted-foreground"}`}
                  onClick={() => setMode("register")}
                  data-testid="tab-register"
                >{t("auth.register")}</button>
              </div>

              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="username">{t("auth.username")}</Label>
                    <div className="relative">
                      <User className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                      <Input id="username" data-testid="input-username" value={form.username} onChange={upd("username")} placeholder="votre_pseudo" className="pl-9 rounded-xl" required minLength={3} />
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="email">{t("auth.email")}</Label>
                  <div className="relative">
                    <Mail className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={upd("email")} placeholder="vous@exemple.com" className="pl-9 rounded-xl" required />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{t("auth.password")}</Label>
                    {mode === "login" && (
                      <button type="button" onClick={() => setMode("forgot")} className="text-xs text-primary hover:underline" data-testid="forgot-link">
                        {t("auth.forgot")}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <KeyRound className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input id="password" type="password" data-testid="input-password" value={form.password} onChange={upd("password")} placeholder="••••••••" className="pl-9 rounded-xl" required minLength={6} />
                  </div>
                </div>

                {mode === "register" && (
                  <>
                    {/* KYC ID upload */}
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5"><IdCard className="h-4 w-4" /> {t("auth.idUpload")}</Label>
                      <input ref={idRef} type="file" accept="image/*" className="hidden" onChange={onIdFile} data-testid="kyc-id-input" />
                      <button
                        type="button"
                        onClick={() => idRef.current?.click()}
                        className={`w-full rounded-xl border-2 border-dashed px-3 py-3 text-sm flex items-center justify-center gap-2 transition-colors ${idImage ? "border-[hsl(160_84%_32%)] text-[hsl(160_84%_32%)] bg-[hsla(160,84%,32%,0.06)]" : "border-border text-muted-foreground hover:border-primary"}`}
                        data-testid="kyc-id-upload"
                      >
                        {idImage ? <><Check className="h-4 w-4" /> {t("auth.idAdded")}</> : <><IdCard className="h-4 w-4" /> {t("auth.idAdd")}</>}
                      </button>
                      <p className="text-[11px] text-muted-foreground">{t("auth.idUploadHint")}</p>
                    </div>

                    <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
                      <input type="checkbox" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} data-testid="age-check" className="mt-0.5 accent-[hsl(214_100%_62%)]" />
                      <span>{t("auth.age")}</span>
                    </label>
                  </>
                )}

                <Button type="submit" className="w-full rounded-xl" disabled={busy} data-testid="auth-submit">
                  {busy ? t("common.pleaseWait") : mode === "login" ? t("auth.signIn") : t("auth.createAccount")}
                </Button>
              </form>
              <p className="text-[11px] text-muted-foreground text-center mt-4">
                {t("auth.onlyAdults")}
              </p>
            </>
          )}
          <LegalFooter className="mt-5 pt-4 border-t border-border" />
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
